import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import set from "lodash/set";
import has from "lodash/has";
import isEqual from "lodash/isEqual";
import { diff } from "deep-object-diff";
import { getStore } from "@store";
import {
  isEmptyObject,
  stringifyNestedKeyPaths,
} from "@utils/object-helpers";
import { getJWT } from "@utils/browser-helpers";
import { formatSypiLoggedChanges } from "@features/sypi/_utils/sypi-interface-helpers";
import {
  generateBlankConfig,
} from "@features/sypi/_utils/sypi-interface-helpers";
import { CONFIG_SCHEDULER, SYPI_S3 } from "@actions/types";
import { STATE_CONTANTS } from "@constants";
import {
  showSpinner,
  stopSpinner,
  reportError,
  setAlertDock,
  setConfirmationDock,
  setToastMessage,
  clickTab,
  enableSave,
  logChangedKey,
  recordChangeObject,
  clearChanges,
  clearLog,
  closeDock,
} from "@actions/global";
import {
  loadSypiSectionPreview,
  setSypiSectionSummaryName,
  toggleSummaryModal,
  setSummaryViewType,
  clearPreview,
  enableSummaryPostAction,
  loadImportedInputs,
  isScheduleChanges,
  loadDefaultConfig,
} from "@features/sypi/actions";
import {
  SYPI_SECTION_SETTERS,
  getSummaryPreview,
} from "./sypi-shared";
import {
  fetchConfigByCTID,
  fetchSypiSchema,
  fetchSypiConfig,
  updateLiveSypiConfig,
} from "./sypi-config";
import { dispatchErrors } from "./sypi-errors";
import { handleChangeReleaseVersion } from "./sypi-init";
import {
  sendFinalReport,
} from "./sypi-errors";

/**
 * SyPI import / update / confirmation flows.
 *
 * `askConfirmation` is the interactive branch — user-confirmable actions
 * route through a confirmation dock; auto-confirmable ones delegate straight
 * to `confirmSypiConfigAction`.
 *
 * `confirmSypiConfigAction` is the central dispatcher: every scheduler
 * action (updateScheduled, deleteDraft, editScheduled, updateFMLinks, …)
 * lands here and fans out to the appropriate flow.
 */

export const handleSypiImportConfigRequest = async (
  importedCTID,
  importedReleaseVersion,
  configToCopy,
  initialConfig,
  applyCallback,
  contentSection = null,
  excludeSections = [],
  store = getStore()
) => {
  store.dispatch(showSpinner());
  store.dispatch(
    clickTab(STATE_CONTANTS.DISPLAY_IMPORT_SUMMARY, CONFIG_SCHEDULER)
  );
  const { activeReleaseVersion } = store.getState().sypiSchema;
  const importedConfig = await fetchConfigByCTID(
    importedReleaseVersion,
    importedCTID
  );

  if (
    importedConfig?.sypi_config &&
    !isEmptyObject(importedConfig.sypi_config)
  ) {
    let importedConfigSection =
      configToCopy === "entire"
        ? importedConfig.sypi_config
        : importedConfig.sypi_config[configToCopy];
    importedConfigSection = contentSection
      ? importedConfigSection[contentSection]
      : importedConfigSection;
    let initialConfigCopy = cloneDeep(initialConfig);

    const updatedImportedConfigSection = excludeSections?.length
      ? excludeSections.reduce((acc, path) => {
          const initialValue = get(initialConfigCopy, path);
          const brokenPaths =
            configToCopy === "entire" ? path : path.split(`${configToCopy}.`);
          const updatedPath = brokenPaths?.[brokenPaths?.length - 1];
          set(acc, updatedPath, initialValue);
          return acc;
        }, importedConfigSection)
      : importedConfigSection;

    let updatedConfig = await getSummaryPreview(
      initialConfigCopy,
      updatedImportedConfigSection,
      undefined,
      configToCopy,
      contentSection
    );

    const ctid = `${getJWT().ctid}`;
    store.dispatch(
      loadSypiSectionPreview(
        updatedConfig,
        configToCopy,
        ctid,
        activeReleaseVersion,
        importedCTID,
        importedReleaseVersion,
        "preview"
      )
    );
    store.dispatch(
      setSypiSectionSummaryName(STATE_CONTANTS.DISPLAY_IMPORT_SUMMARY)
    );
    store.dispatch(toggleSummaryModal(true));
  } else if (importedConfig?.messages?.length) {
    store.dispatch(
      // eslint-disable-next-line no-undef
      reportError(messages.map((err) => err.message).join(" "))
    );
  } else {
    store.dispatch(
      reportError(`No config file found for CTID ${importedCTID}.`)
    );
  }
  store.dispatch(stopSpinner());
};

export const handleSypiUpdateConfigRequest = async (
  scheduleNewDefaults = false,
  importedConfig = null,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  if (!scheduleNewDefaults)
    store.dispatch(
      clickTab(STATE_CONTANTS.DISPLAY_UPDATE_DEFAULTS_SUMMARY, CONFIG_SCHEDULER)
    );
  const {
    sypiSchema: { activeReleaseVersion },
    sypiNewView: { initialConfig, errors },
  } = store.getState();
  const release_version = importedConfig
    ? importedConfig.release_version
    : activeReleaseVersion;
  const initial_config = importedConfig ? importedConfig.config : initialConfig;

  const sypiSchema = await fetchSypiSchema(release_version);
  const { formattedMasterSchema, formattedSchema } = sypiSchema;
  let clientConfigTemplate = generateBlankConfig(formattedSchema);
  let masterConfigTemplate = generateBlankConfig(formattedMasterSchema);
  const clientConfig = await fetchSypiConfig(release_version);
  const sypiClientConfig =
    typeof clientConfig === "string"
      ? clientConfigTemplate
      : clientConfig.sypi_config;
  const savedConfigChanges = diff(clientConfigTemplate, sypiClientConfig);
  const schemaHasChanged = diff(clientConfigTemplate, masterConfigTemplate);
  const schemaChanges = diff(formattedSchema, formattedMasterSchema);
  const pathConfigChanges =
    savedConfigChanges && Object.entries(savedConfigChanges).length !== 0
      ? stringifyNestedKeyPaths(savedConfigChanges)
      : [];

  if (
    (schemaHasChanged && Object.entries(schemaHasChanged).length !== 0) ||
    (!isEmptyObject(errors) && Object.entries(errors).length !== 0) ||
    (schemaChanges && Object.entries(schemaChanges).length !== 0)
  ) {
    let updatedSypiConfig =
      pathConfigChanges && pathConfigChanges.length
        ? pathConfigChanges.reduce((accSypiConfig, path) => {
            if (has(accSypiConfig, path) && has(sypiClientConfig, path)) {
              const savedValue = get(sypiClientConfig, path);
              set(accSypiConfig, path, savedValue);
            }
            return accSypiConfig;
          }, masterConfigTemplate)
        : masterConfigTemplate;

    if (Object.entries(updatedSypiConfig).length !== 0) {
      const diffMasterConfig = diff(initial_config, updatedSypiConfig);
      getSummaryPreview(
        initial_config,
        diffMasterConfig,
        updatedSypiConfig,
        undefined,
        undefined,
        scheduleNewDefaults,
        release_version
      );
      if (!scheduleNewDefaults) store.dispatch(toggleSummaryModal(true));
    }
    dispatchErrors(updatedSypiConfig, formattedMasterSchema);
  } else if (!scheduleNewDefaults) {
    store.dispatch(
      setAlertDock({
        title: "New Schema Configuration Defaults",
        message: "No config or schemas changes detected.",
      })
    );
    store.dispatch(clickTab(STATE_CONTANTS.SCHEDULER_STATUS, CONFIG_SCHEDULER));
  }
  store.dispatch(stopSpinner());
};

export const askConfirmation = async (askDetails, store = getStore()) => {
  const {
    action,
    pageContext,
    hasChanged,
    formattedItemName,
    loggedChanges,
    keyTarget,
  } = askDetails;
  if (
    action === "updatedSchemaDefaults" ||
    action === "applyImportedPreviewChanges"
  ) {
    confirmSypiConfigAction(null, action, askDetails);
  } else if (action === "changeReleaseVersion") {
    if (hasChanged) {
      store.dispatch(
        setConfirmationDock({
          idTarget: keyTarget,
          title: "This page contains unsaved changes.",
          message: (
            <>
              <div style={{ fontFamily: "SynchronySans" }}>
                All unsaved changes will be lost if you navigate away from this
                page. To save your progress, create a new draft or schedule a
                config change before navigating away.'
              </div>
              <div style={{ fontFamily: "SynchronySans" }}>
                To go back to editing the current Release Version, click
                'Cancel'. To navigate away, click 'Confirm' below.
              </div>
            </>
          ),
          updateAction: action,
        })
      );
    } else {
      confirmSypiConfigAction(keyTarget, action, askDetails);
    }
  } else if (hasChanged && action === "cancel") {
    let formattedLoggedChanges = formatSypiLoggedChanges(loggedChanges);
    store.dispatch(
      setConfirmationDock({
        idTarget: pageContext,
        title: `Undo Changes to ${formattedItemName}`,
        message: `This will undo your current changes to: ${formattedLoggedChanges}. Do you wish to proceed?`,
        updateAction: "cancel",
      })
    );
  } else if (!hasChanged) {
    store.dispatch(
      setAlertDock,
      setToastMessage({
        title: `NO CHANGES FOR ${formattedItemName.toUpperCase()}`,
        message: `${formattedItemName} values have not been revised.`,
      })
    );
  }
};

/**
 * Central dispatcher. `updateAction` is the string key returned by
 * askConfirmation / dock buttons; this switch fans out to the appropriate
 * scheduler / summary / update flow and finally calls `sendFinalReport`
 * with any {type, title, message} produced by the branch.
 */
export const confirmSypiConfigAction = async (
  keyTarget,
  updateAction,
  actionDetails = {},
  includeSchemaDefaultUpdates,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  const storeContents = store.getState();
  const { sypiNewView, sypiConfigScheduler, sypiSchema } = storeContents;
  const { sypiConfigSaveState } = sypiConfigScheduler;
  let confirmedReport = null;

  // Deferred imports break potential module cycles (this module and its
  // callees import back into `sypi-summary`, `sypi-schedule`, `sypi-history`
  // which in turn touch modules that import from here).
  const {
    saveScheduleWithSchemaDefaults,
  } = await import("./sypi-summary");
  const {
    saveScheduledDraftedConfig,
    setScheduledDraftConfig,
    deleteSchecduledDraft,
    updateFMLinks,
  } = await import("./sypi-schedule");
  const { scheduleRevert } = await import("./sypi-history");
  const { updateSchemaConfigDefaults } = await import("./sypi-config");
  const { setSummaryFormRequirements } = await import("./sypi-summary");

  if (updateAction === "cancel") {
    store.dispatch(clearChanges());
    store.dispatch(clearLog());
    updateAction = "cleared";
    store.dispatch(closeDock());
  } else if (updateAction === `${SYPI_S3}UpdateLive`) {
    await updateLiveSypiConfig(keyTarget, sypiNewView, updateAction);
  } else if (
    updateAction === "updateScheduled" ||
    updateAction === "updateDraft"
  ) {
    confirmedReport = includeSchemaDefaultUpdates
      ? await saveScheduleWithSchemaDefaults()
      : sypiConfigScheduler.configSchedulerStatus ===
        STATE_CONTANTS.DISPLAY_REVERT_SUMMARY
      ? await scheduleRevert()
      : await saveScheduledDraftedConfig();
  } else if (updateAction === "editScheduled" || updateAction === "editDraft") {
    const isScheduled = updateAction === "editScheduled";
    confirmedReport = await setScheduledDraftConfig(
      keyTarget,
      isScheduled,
      actionDetails.release_version
    );
  } else if (
    updateAction === "deleteScheduled" ||
    updateAction === "deleteDraft" ||
    updateAction === "deleteCompleted"
  ) {
    const deleteStatus =
      updateAction === "deleteScheduled"
        ? "scheduled"
        : updateAction === "deleteDraft"
        ? "draft"
        : "completed";
    confirmedReport = await deleteSchecduledDraft(keyTarget, deleteStatus);
  } else if (
    updateAction === "scheduledSummary" ||
    updateAction === "draftSummary" ||
    updateAction === "completedSummary"
  ) {
    if (updateAction === "scheduledSummary") {
      store.dispatch(isScheduleChanges(true));
      if (
        keyTarget === "summary" &&
        sypiConfigSaveState !== "updateSchema" &&
        sypiConfigSaveState !== "edit"
      ) {
        let checkForSchemaDefaults = true;
        await handleSypiUpdateConfigRequest(checkForSchemaDefaults);
      }
    }
    if (
      !(
        STATE_CONTANTS.DISPLAY_REVERT_SUMMARY ===
        sypiConfigScheduler.configSchedulerStatus
      )
    ) {
      confirmedReport = await setSummaryFormRequirements(
        keyTarget,
        updateAction,
        sypiSchema.activeReleaseVersion
      );
    } else {
      store.dispatch(setSummaryViewType(keyTarget));
      store.dispatch(toggleSummaryModal(true));
    }
  } else if (updateAction === "updateFMLinks") {
    confirmedReport = await updateFMLinks();
  } else if (updateAction === "updatedSchemaDefaults") {
    confirmedReport = await updateSchemaConfigDefaults(actionDetails);
  } else if (updateAction === "changeReleaseVersion") {
    confirmedReport = await handleChangeReleaseVersion(keyTarget);
  } else if (updateAction === "applyImportedPreviewChanges") {
    confirmedReport = await applyImportedConfig();
  }

  store.dispatch(enableSave());
  store.dispatch(stopSpinner());
  if (confirmedReport) {
    sendFinalReport(confirmedReport);
  }
};

export const applyImportedConfig = async (store = getStore()) => {
  store.dispatch(showSpinner());
  const { sypiConfigScheduler, sypiNewView, sypiSchema } = store.getState();
  const { sypiSection, sypiSectionConfigs, summaryView } =
    sypiConfigScheduler.generalPreview;
  const { sypiConfig, initialConfig } = sypiNewView;
  let setConfigCallback = SYPI_SECTION_SETTERS?.[sypiSection];
  // Avoid any override due to object reference
  let sypiConfigCopy = cloneDeep(sypiConfig);
  const sypiSectionToApply = cloneDeep(sypiSectionConfigs);
  const pathStringToApply = stringifyNestedKeyPaths(sypiSectionToApply);
  const updatedSypiSectionsCopy = pathStringToApply.reduce(
    (sypiConfigCopyAcc, path) => {
      const correctedPath =
        sypiSection === "entire" ? path : `${sypiSection}.${path}`;
      if (has(sypiConfigCopy, correctedPath)) {
        set(sypiConfigCopyAcc, path, get(sypiSectionToApply, path));
      }
      return sypiConfigCopyAcc;
    },
    {}
  );
  let importedDiff = {};
  if (sypiSection === "entire") {
    sypiConfigCopy = updatedSypiSectionsCopy;
    importedDiff = sypiConfig;
  } else {
    sypiConfigCopy[sypiSection] = updatedSypiSectionsCopy;
    importedDiff = sypiConfig[sypiSection];
  }
  const updatedImportDiff = diff(importedDiff, updatedSypiSectionsCopy);
  const updatedImportedStringPaths = stringifyNestedKeyPaths(updatedImportDiff);
  const updatedImportConfig = updatedImportedStringPaths.reduce(
    (importAcc, path) => {
      const correctedPath =
        sypiSection === "entire" ? path : `${sypiSection}.${path}`;
      const oldValue = get(importedDiff, path, "");
      const newValue = get(updatedSypiSectionsCopy, path, "");
      if (!isEqual(newValue, oldValue)) {
        importAcc[correctedPath] = newValue;
      }
      return importAcc;
    },
    {}
  );
  const updatedConfigDiff = diff(initialConfig, sypiConfigCopy);
  const updatedConfigStringPaths = stringifyNestedKeyPaths(updatedConfigDiff);
  const updatedChangeDict = updatedConfigStringPaths.reduce(
    (configAcc, path) => {
      const oldValue = get(initialConfig, path, "");
      const newValue = get(sypiConfigCopy, path, "");
      if (!isEqual(newValue, oldValue)) {
        configAcc[path] = newValue;
      }
      return configAcc;
    },
    {}
  );
  if (setConfigCallback) {
    store.dispatch(setConfigCallback(updatedSypiSectionsCopy));
    store.dispatch(logChangedKey(Object.keys(updatedChangeDict)));
    store.dispatch(recordChangeObject(updatedChangeDict));
    store.dispatch(loadImportedInputs(updatedImportConfig));
  }

  store.dispatch(clickTab(STATE_CONTANTS.SCHEDULER_STATUS, CONFIG_SCHEDULER));
  store.dispatch(setSummaryViewType(STATE_CONTANTS.SUMMARY));
  store.dispatch(clearPreview());
  store.dispatch(enableSummaryPostAction(false));
  dispatchErrors(sypiConfigCopy, sypiSchema.clientSchema);
  store.dispatch(stopSpinner());
  return {
    type: "ALERT_DOCK",
    title: `Imported Config Confirmed`,
    message: `New configs have been applied to sypi ${
      sypiSection === "entire" ? "config" : `${sypiSection}`
    }. Review and save your work by scheduling your changes or creating a draft.`,
  };
};
